// Accessible image lightbox for the art exhibit gallery.
//
// Mirrors the core WordPress image lightbox UX (dialog, focus trap, Esc,
// prev/next, scroll lock, white scrim + dark controls, focus return) but is
// driven here directly so it works reliably inside the Swiper carousel.

const CLOSE_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false"><path fill="currentColor" d="m13.06 12 6.47-6.47-1.06-1.06L12 10.94 5.53 4.47 4.47 5.53 10.94 12l-6.47 6.47 1.06 1.06L12 13.06l6.47 6.47 1.06-1.06L13.06 12Z"/></svg>';
const PREV_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false"><path fill="currentColor" d="M14.6 7l-1.2-1L8 12l5.4 6 1.2-1-4.6-5z"/></svg>';
const NEXT_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false"><path fill="currentColor" d="M10.6 6L9.4 7l4.6 5-4.6 5 1.2 1 5.4-6z"/></svg>';

export default function initArtLightbox() {
  const galleries = document.querySelectorAll(".art-detail__gallery");
  if (!galleries.length) return;

  const overlay = document.createElement("div");
  overlay.className = "art-lightbox";
  overlay.hidden = true;
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Enlarged image");
  overlay.setAttribute("tabindex", "-1");
  overlay.innerHTML =
    '<div class="art-lightbox__scrim"></div>' +
    '<button type="button" class="art-lightbox__close" aria-label="Close">' + CLOSE_ICON + "</button>" +
    '<button type="button" class="art-lightbox__nav art-lightbox__nav--prev" aria-label="Previous image">' + PREV_ICON + "</button>" +
    '<figure class="art-lightbox__stage"><img class="art-lightbox__img" alt=""></figure>' +
    '<button type="button" class="art-lightbox__nav art-lightbox__nav--next" aria-label="Next image">' + NEXT_ICON + "</button>";
  document.body.appendChild(overlay);

  const imgEl = overlay.querySelector(".art-lightbox__img");
  const scrim = overlay.querySelector(".art-lightbox__scrim");
  const closeBtn = overlay.querySelector(".art-lightbox__close");
  const prevBtn = overlay.querySelector(".art-lightbox__nav--prev");
  const nextBtn = overlay.querySelector(".art-lightbox__nav--next");

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let triggers = [];
  let index = 0;
  let lastFocused = null;
  let scrollY = 0;

  function focusable() {
    return [prevBtn, nextBtn, closeBtn].filter((b) => !b.hidden);
  }

  function render(i) {
    index = (i + triggers.length) % triggers.length;
    const t = triggers[index];
    imgEl.src = t.dataset.full;
    if (t.dataset.srcset) {
      imgEl.srcset = t.dataset.srcset;
      imgEl.sizes = "100vw";
    } else {
      imgEl.removeAttribute("srcset");
    }
    imgEl.alt = t.dataset.alt || "";
    overlay.setAttribute("aria-label", t.dataset.alt || "Enlarged image");

    const multiple = triggers.length > 1;
    prevBtn.hidden = !multiple;
    nextBtn.hidden = !multiple;
  }

  function lockScroll() {
    scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
  }

  function unlockScroll() {
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.right = "";
    document.body.style.width = "";
    window.scrollTo(0, scrollY);
  }

  function setInert(on) {
    Array.from(document.body.children).forEach((el) => {
      if (el === overlay) return;
      if (on) el.setAttribute("inert", "");
      else el.removeAttribute("inert");
    });
  }

  function open(gallery, trigger) {
    triggers = Array.from(gallery.querySelectorAll(".lightbox-trigger"));
    lastFocused = trigger;
    render(triggers.indexOf(trigger));
    lockScroll();
    setInert(true);
    overlay.hidden = false;
    // Force reflow so the opening transition runs.
    void overlay.offsetWidth;
    overlay.classList.add("is-open");
    closeBtn.focus();
    document.addEventListener("keydown", onKeydown);
  }

  let closing = false;
  function finishClose() {
    if (!closing) return;
    closing = false;
    overlay.hidden = true;
    imgEl.removeAttribute("src");
    imgEl.removeAttribute("srcset");
    setInert(false);
    unlockScroll();
    document.removeEventListener("keydown", onKeydown);
    if (lastFocused) lastFocused.focus();
  }

  function close() {
    if (overlay.hidden || closing) return;
    closing = true;
    overlay.classList.remove("is-open");
    if (reduceMotion) {
      finishClose();
    } else {
      let done = false;
      const onEnd = (e) => {
        if (e.target !== overlay || done) return;
        done = true;
        overlay.removeEventListener("transitionend", onEnd);
        finishClose();
      };
      overlay.addEventListener("transitionend", onEnd);
      // Fallback in case transitionend doesn't fire.
      setTimeout(() => {
        if (done) return;
        done = true;
        overlay.removeEventListener("transitionend", onEnd);
        finishClose();
      }, 400);
    }
  }

  function onKeydown(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "ArrowLeft" && triggers.length > 1) {
      e.preventDefault();
      render(index - 1);
    } else if (e.key === "ArrowRight" && triggers.length > 1) {
      e.preventDefault();
      render(index + 1);
    } else if (e.key === "Tab") {
      const f = focusable();
      if (!f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  galleries.forEach((gallery) => {
    gallery.addEventListener("click", (e) => {
      const figure = e.target.closest(".art-lightbox__figure");
      if (!figure) return;
      const trigger = figure.querySelector(".lightbox-trigger");
      if (!trigger) return;
      e.preventDefault();
      open(gallery, trigger);
    });
  });

  closeBtn.addEventListener("click", close);
  scrim.addEventListener("click", close);
  prevBtn.addEventListener("click", () => render(index - 1));
  nextBtn.addEventListener("click", () => render(index + 1));
}
